import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { DirectDebitForm } from '../../components/finance/DirectDebitForm';
import { toast } from 'sonner';
import { DirectDebitSettings } from '../../types/direct-debit';
import { financeApi } from '../../utils/finance-api';
import { api } from '../../utils/api';
import { RefreshCw, Calculator, ChevronLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import AccessRestricted from '../../components/AccessRestricted';

const DirectDebitCreatePage = () => {
  const navigate = useNavigate();
  const ability = useAbility(AbilityContext);
  const canCreate = ability.can(ACTIONS.CREATE, RESOURCES.DIRECT_DEBIT);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [settings, setSettings] = useState<DirectDebitSettings | null>(null);

  useEffect(() => {
    if (canCreate) loadSettings();
  }, [canCreate]);

  if (!canCreate) {
    return (
      <AccessRestricted
        navigateTo="/finance/direct-debit"
        backMessage="Back to Direct Debit Register"
        accessMessage="You don't have permission to create direct debit records."
      />
    );
  }

  const loadSettings = async () => {
    try {
      const [companies, departments] = await Promise.all([
        financeApi.listCompanies(),
        // Selection endpoint, not /api/departments: finance roles do not hold
        // read:department_list and would 403 (same fix as the edit page).
        api.get<{ departments: any[] }>('/api/departments/selection')
      ]);
      
      setSettings({
        id: 'current',
        authorizedUsers: [],
        companies: companies,
        departments: departments.departments || departments, // Handle both {departments: []} and [] formats
        currencies: ['AUD', 'USD', 'EUR', 'GBP'],
        frequencies: ['weekly', 'fortnightly', 'monthly', 'quarterly', 'annually', 'ad-hoc'],
        defaultSettings: {
          currency: 'AUD',
          frequency: 'monthly',
          approvalRequired: true
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      toast.error("Failed to load finance settings");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (payload: any, selectedFiles: File[]) => {
    setIsSaving(true);
    try {
      const response: any = await financeApi.createDirectDebit(payload);
      const ddId = response?.id;

      if (ddId && selectedFiles.length > 0) {
        for (const file of selectedFiles) {
          const uploadFormData = new FormData();
          uploadFormData.append('image', file);
          uploadFormData.append('context', 'finance');

          try {
            const uploadResult: any = await api.post('/api/upload/s3', uploadFormData, { skipErrorToast: true });

            if (uploadResult.url) {
              await financeApi.addAttachment({
                directDebitId: ddId,
                fileName: file.name,
                url: uploadResult.url,
                fileKey: uploadResult.key || uploadResult.filename,
                fileSize: uploadResult.size,
                fileType: uploadResult.mimetype,
                category: 'other'
              });
            }
          } catch (uploadError) {
            console.error(`Error uploading ${file.name}:`, uploadError);
            toast.error(`Failed to upload ${file.name}`);
          }
        }
      }

      toast.success("Direct debit record created successfully");
      navigate('/finance/direct-debit');
    } catch (error: any) {
      toast.error(error.message || "Failed to create direct debit record");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="h-6 w-6 animate-spin text-blue-600 mr-2" />
        <span>Loading Configuration...</span>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-start gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/finance/direct-debit')} className="h-9 w-9 mt-1">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Add New Direct Debit</h1>
            <p className="text-slate-500 mt-1">Add a new direct debit transaction</p>
          </div>
        </div>
      </div>
      
      <DirectDebitForm 
        onSubmit={handleSubmit}
        onCancel={() => navigate('/finance/direct-debit')}
        isLoading={isSaving}
        settings={settings}
      />
    </div>
  );
};

export default DirectDebitCreatePage;
